import {
  CONFIG_CACHE_DB_NAME,
  CONFIG_CACHE_STORE_NAME,
  CONFIG_CACHE_KEY,
  CONFIG_CACHE_OPEN_TIMEOUT_MS,
} from '../constants/bookingConst';

/**
 * A browser-side cache for the club config, so a returning visitor does not
 * re-download it on every page load.
 *
 * WHY IndexedDB AND NOT localStorage: the config carries every image the club
 * has uploaded as a base64 data URL — logo, QR codes, gallery, landing, about
 * and per-sport photos. localStorage is capped at roughly 5MB per origin and
 * throws QuotaExceededError past it, which is exactly the club whose config is
 * worth caching. IndexedDB has no comparable ceiling.
 *
 * Everything here fails soft: a blocked or unavailable store returns null and
 * the caller falls back to reading Firebase, which is the behaviour this cache
 * replaces. Nothing in the app may depend on a cache hit.
 *
 * The cached value is the RAW Firebase snapshot, not the merged config object.
 * DEFAULT_CONFIG changes between deployments, so the defaults are re-merged on
 * read — caching the merged shape would pin a visitor to the defaults that
 * existed when they last fetched.
 */

/**
 * Opens (and if needed creates) the cache database.
 *
 * Races the open against a timeout: an IndexedDB open blocks indefinitely when
 * another tab holds an older version open, and this sits on the app's boot
 * path — a stuck open must degrade to "no cache", never to a hung page.
 *
 * @returns {Promise<IDBDatabase|null>} the database, or null if unavailable.
 */
const openCacheDb = () => new Promise((resolve) => {
  if (typeof indexedDB === 'undefined') {
    resolve(null);
    return;
  }

  let settled = false;
  const finish = (value) => {
    if (settled) return;
    settled = true;
    resolve(value);
  };

  const timer = setTimeout(() => finish(null), CONFIG_CACHE_OPEN_TIMEOUT_MS);

  try {
    const request = indexedDB.open(CONFIG_CACHE_DB_NAME, 1);

    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(CONFIG_CACHE_STORE_NAME)) {
        database.createObjectStore(CONFIG_CACHE_STORE_NAME);
      }
    };
    request.onsuccess = () => { clearTimeout(timer); finish(request.result); };
    request.onerror = () => { clearTimeout(timer); finish(null); };
    request.onblocked = () => { clearTimeout(timer); finish(null); };
  } catch (error) {
    clearTimeout(timer);
    finish(null);
  }
});

/**
 * Runs one transaction against the cache store.
 *
 * @param {IDBTransactionMode} mode - 'readonly' or 'readwrite'.
 * @param {(store: IDBObjectStore) => IDBRequest} run - issues the request.
 * @returns {Promise<any|null>} the request result, or null on any failure.
 */
const withStore = async (mode, run) => {
  const database = await openCacheDb();
  if (!database) return null;

  return new Promise((resolve) => {
    try {
      const transaction = database.transaction(CONFIG_CACHE_STORE_NAME, mode);
      const request = run(transaction.objectStore(CONFIG_CACHE_STORE_NAME));
      request.onsuccess = () => resolve(request.result ?? null);
      request.onerror = () => resolve(null);
      transaction.onabort = () => resolve(null);
      transaction.oncomplete = () => database.close();
    } catch (error) {
      resolve(null);
    }
  });
};

/**
 * Reads the cached config entry.
 *
 * @returns {Promise<{config: object, lastModified: number|null, cachedAt: number}|null>}
 *   the entry, or null when nothing usable is cached.
 */
export const readCachedConfig = async () => {
  const entry = await withStore('readonly', store => store.get(CONFIG_CACHE_KEY));
  if (!entry || typeof entry !== 'object' || !entry.config) return null;
  return entry;
};

/**
 * Replaces the cached config entry.
 *
 * @param {object} config - the RAW config snapshot value from Firebase.
 * @param {number|null} lastModified - that snapshot's lastModified stamp.
 * @returns {Promise<boolean>} whether the write landed.
 */
export const writeCachedConfig = async (config, lastModified) => {
  if (!config || typeof config !== 'object') return false;
  const entry = { config, lastModified: lastModified ?? null, cachedAt: Date.now() };
  const result = await withStore('readwrite', store => store.put(entry, CONFIG_CACHE_KEY));
  return result !== null;
};

/**
 * Drops the cached config. Called when a write fails partway, so a visitor is
 * never left holding a config that does not match the database.
 *
 * @returns {Promise<void>}
 */
export const clearCachedConfig = async () => {
  await withStore('readwrite', store => store.delete(CONFIG_CACHE_KEY));
};
